<template>
  <header class="header">
    <div
      v-if="appConfig.demoWarn && appConfig.demoWarnText?.length"
      class="header__warning"
    >
      <v-icon size="28">
        bi-exclamation-triangle
      </v-icon>
      <p class="header__warning__text">
        {{ appConfig.demoWarnText }}
      </p>
    </div>

    <div
      class="header__main"
      :style="{ 'background-color': `${appConfig.color.mainDark} !important` }"
    >
      <div
        v-show="!mobileView"
        class="header__logo logo-wrap"
        data-test="logo-wrap"
        @click="onLogoClick"
      >
        <img
          v-if="appConfig.logoURL"
          class="logo-wrap__img"
          :src="appConfig.logoURL"
          alt="Крипто-Про"
          data-test="logo-custom"
        >
        <LogoCryptopro
          v-else
          data-test="logo-default"
        />
      </div>

      <v-tabs
        v-if="!lockButtons"
        :value="activeTab"
        class="header__tabs"
        style="justify-self: center;"
        optional
        background-color="transparent"
        :height="height"
        :grow="mobileView"
        :vertical="mobileView"
      >
        <v-tabs-slider
          v-if="activeTab >= 0 && activeTab < tabs.length"
          class="header__tabs-slider"
          :style="{ 'background-color': appConfig.color.mainLight }"
        />
        <v-tab
          v-for="tab in tabs"
          :key="tab.id"
          data-test="nav-tabs"
          class="header__tab pl-5 pr-5"
          active-class="header__tab--active"
          :style="{ 'color': appConfig.color.textLight }"
          :to="tab.route"
        >
          <span class="header__tab__text">{{ tab.title }}</span>
        </v-tab>
      </v-tabs>

      <v-btn
        v-if="appConfig.helpURL"
        :disabled="lockButtons"
        dark
        tile
        :height="height"
        elevation="0"
        :color="appConfig.color.mainDark"
        class="header__help"
        :class="{ 'header__help--active': activeTab === 2 }"
        @click="onHelpClick"
      >
        <v-tooltip
          v-if="mobileView"
          bottom
        >
          <template #activator="{ on }">
            <v-icon
              v-on="on"
              size="40px"
            >
              bi-question
            </v-icon>
          </template>
          <span>Справка</span>
        </v-tooltip>

        <span v-else>Справка</span>
      </v-btn>
    </div>
  </header>
</template>

<script>
import LogoCryptopro from '@/components/svg/LogoCryptopro'
import { mapActions, mapState } from 'vuex'
import { apiResetAllDocuments } from '@/api/api'

export default {
  name: 'MainHeader',
  components: {
    LogoCryptopro
  },

  props: {
    lockButtons: {
      type: Boolean,
      require: false,
      default: false
    },
    height: {
      type: Number,
      require: false,
      default: 75
    },
    mobileView: {
      type: Boolean,
      require: false,
      default: false
    }
  },

  data () {
    return {
      tabs: [
        { title: 'Проверка подписи', id: 0, route: '/signature' },
        { title: 'Проверка сертификата', id: 1, route: '/certificate' }
      ],
      appConfig: this.$appConfig
    }
  },

  computed: {
    ...mapState({
      storedActiveTab: state => state.activeTab
    }),

    activeTab: {
      get () {
        return this.storedActiveTab
      },
      set (tab) {
        this.changeTab(tab)
      }
    }
  },

  methods: {
    ...mapActions(['resetStateByType', 'changeTab']),

    async onLogoClick () {
      this.resetStateByType('signature')
      this.resetStateByType('certificate')
      const reset = await apiResetAllDocuments()
      if (!reset) {
        this.$router.go(0)
      }
    },

    onHelpClick () {
      if (this.$router.currentRoute.name !== 'Help') {
        this.$router.push('/help')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  .header {
    position: sticky;
    left: 0;
    top: 0;
    box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12);
    z-index: 5;

    &__warning {
      display: flex;
      justify-content: center;
      align-items: center;
      column-gap: 15px;
      padding: 2px 20px;
      background-color: $color-warning;
      pointer-events: none;

      @media(max-width: $cp-laptop-breakpoint) {
        padding: 2px 10px;
      }

      &__text {
        font-size: 14px;
        text-align: left;
        margin: 0;

        @media(max-width: $cp-laptop-breakpoint) {
          font-size: 12px;
        }
      }
    }

    &__main {
      position: relative;
      display: flex;
      justify-content: center;
      background-color: $color-main-dark !important;

      @media(max-width: $cp-laptop-breakpoint) {
        display: block;
      }
    }

    &__tabs {
      flex: 0;
    }

    &__tabs-slider {
      background-color: $color-main-light;

      @media(max-width: $cp-laptop-breakpoint) {
        width: 3px;
      }
    }

    &__tab {
      min-width: 265px;
      text-transform: uppercase;

      & .header__tab__text {
        opacity: 0.75;
      }

      &--active {
        cursor: default;

        &:before {
          opacity: 0 !important;
        }

        & .header__tab__text {
          opacity: 1;
        }
      }
    }

    &__logo {
      position: absolute;
      width: 150px;
      height: 50px;
      left: 100px;
      top: 50%;
      transform: translateY(-50%);

      &:hover {
        cursor: pointer;
      }
    }

    &__help {
      position: absolute;
      right: 100px;
      top: 0;

      &--active {
        &:before {
          content: '';
          background-color: currentColor;
          left: 0;
          top: 0;
          opacity: 0.08;
          pointer-events: none;
          transition: opacity 0.2s cubic-bezier(0.4, 0, 0.6, 1);
        }
      }

      @media(max-width: $cp-laptop-breakpoint) {
        right: 0;
        top: 0;
      }
    }
  }

  .logo-wrap {
    &__img {
      width: auto;
      max-width: 250px;
      height: 50px;
    }
  }
</style>
